import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  SAUDI_PHONE_COUNTRY,
  findPhoneCountryForE164,
  isValidNationalNumber,
  toNationalDigits,
  type PhoneCountry,
} from "@/lib/phoneCountries";

// The ONE phone field of the app: dial-code dropdown + national-number box,
// E.164 in and out. Saudi-only by default — the API's IsSaudiPhone validator
// (auth, tenants, owners, …) only accepts +9665XXXXXXXX. Surfaces that may
// legitimately collect a non-Saudi number (the public interest form, whose OTP
// goes through POST /leads/public/request-otp) opt in by passing `countries`
// (see apps/shared/src/phone-countries.ts).
const SAUDI_ONLY: readonly PhoneCountry[] = [SAUDI_PHONE_COUNTRY];

interface PhoneInputProps {
  /** E.164 (`+9665XXXXXXXX`), or "" when empty. */
  value: string;
  /** Emits E.164, or "" once the number box is cleared. */
  onChange: (fullNumber: string, countryCode: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
  onEnter?: () => void;
  /** Countries offered in the dial-code dropdown. Default: Saudi Arabia only. */
  countries?: readonly PhoneCountry[];
  /** id of the number input — lets a <label htmlFor> point at it. */
  id?: string;
  placeholder?: string;
  /** Caller-owned error shown under the field (replaces the built-in hint). */
  error?: string;
  /** Suppress the built-in "invalid number" hint when the caller renders its own. */
  hideInvalidHint?: boolean;
  /** Accept any 9-digit Saudi number (landlines) — company/organisation phones only. */
  allowLandline?: boolean;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "data-testid"?: string;
}

export function PhoneInput({
  value,
  onChange,
  onBlur,
  disabled,
  className,
  autoFocus,
  onEnter,
  countries = SAUDI_ONLY,
  id,
  placeholder,
  error,
  hideInvalidHint,
  allowLandline,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "data-testid": testId,
}: PhoneInputProps) {
  const { t } = useLanguage();
  const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(
    () => findPhoneCountryForE164(value, countries) ?? countries[0],
  );
  const [touched, setTouched] = useState(false);

  // An edit form that loads a number from another offered country follows it.
  const valueCountry = findPhoneCountryForE164(value, countries);
  useEffect(() => {
    if (valueCountry && valueCountry !== selectedCountry) setSelectedCountry(valueCountry);
  }, [valueCountry, selectedCountry]);

  const country = valueCountry ?? selectedCountry;
  const national = value.startsWith(country.dialCode) ? value.slice(country.dialCode.length) : "";

  const emit = (c: PhoneCountry, digits: string) =>
    onChange(digits ? c.dialCode + digits : "", c.dialCode);

  const countryName = (code: PhoneCountry["code"]): string => {
    switch (code) {
      case "SA": return t("السعودية", "Saudi Arabia");
      case "AE": return t("الإمارات", "UAE");
      case "KW": return t("الكويت", "Kuwait");
      case "BH": return t("البحرين", "Bahrain");
      case "QA": return t("قطر", "Qatar");
      case "OM": return t("عمان", "Oman");
      case "EG": return t("مصر", "Egypt");
      case "JO": return t("الأردن", "Jordan");
      default: return code;
    }
  };

  const handleCountryChange = (code: string) => {
    const next = countries.find((c) => c.code === code);
    if (!next) return;
    setSelectedCountry(next);
    emit(next, national.slice(0, next.nationalLength));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && onEnter && !disabled) {
      e.preventDefault();
      onEnter();
    }
  };

  const landline = allowLandline && country.code === "SA";
  const valid = landline
    ? /^[1-9]\d{8}$/.test(national)
    : isValidNationalNumber(country, national);
  const invalid = touched && national !== "" && !valid;
  const message = error
    || (invalid && !hideInvalidHint
      ? country.code === "SA" && !landline
        ? t("يجب أن يبدأ رقم الجوال بـ 5 ويتكون من 9 أرقام", "Phone must start with 5 and be 9 digits")
        : t("رقم الجوال غير صحيح", "Invalid phone number")
      : "");

  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex gap-2" dir="ltr">
        <Select dir="ltr" value={country.code} onValueChange={handleCountryChange} disabled={disabled}>
          <SelectTrigger
            className="h-11 w-[7.5rem] flex-shrink-0 rounded-xl"
            aria-label={t("رمز الدولة", "Country code")}
            data-testid="phone-country-trigger"
          >
            <SelectValue>
              <span dir="ltr" className="flex items-center gap-1.5 text-sm font-medium">
                <span aria-hidden="true">{country.flag}</span>
                {country.dialCode}
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {countries.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                <span className="flex items-center gap-2">
                  <span aria-hidden="true">{c.flag}</span>
                  <span dir="ltr" className="font-medium">{c.dialCode}</span>
                  <span className="text-muted-foreground">{countryName(c.code)}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          id={id}
          type="tel"
          placeholder={placeholder ?? (landline ? "XXXXXXXXX" : country.example)}
          value={national}
          onChange={(e) => emit(country, toNationalDigits(e.target.value, country))}
          onBlur={() => {
            setTouched(true);
            onBlur?.();
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={cn("flex-1 text-start", message && "border-destructive focus-visible:ring-destructive/30")}
          dir="ltr"
          autoFocus={autoFocus}
          autoComplete="tel-national"
          inputMode="numeric"
          aria-invalid={ariaInvalid ?? (message ? true : undefined)}
          aria-describedby={ariaDescribedBy}
          data-testid={testId}
        />
      </div>
      {message && <p className="text-xs text-destructive">{message}</p>}
    </div>
  );
}
